import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { Skeleton } from '../../../components/ui/Skeleton';
import { EmptyState } from '../../../components/ui/EmptyState';

interface JsonSchemaProperty {
  type?: string;
  format?: string;
  enum?: string[];
  description?: string;
  title?: string;
}

interface JsonSchema {
  type?: string;
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
}

interface DynamicParamFormProps {
  schema: Record<string, unknown>;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  errors?: Record<string, string>;
}

function parseSchema(raw: Record<string, unknown>): JsonSchema {
  return raw as JsonSchema;
}

function getFieldType(
  prop: JsonSchemaProperty,
): 'date' | 'select' | 'text' | 'number' | 'json' {
  if (!prop.type) return 'json';
  if (prop.type === 'string' && prop.format === 'date') return 'date';
  if (prop.type === 'string' && Array.isArray(prop.enum) && prop.enum.length > 0)
    return 'select';
  if (prop.type === 'string') return 'text';
  if (prop.type === 'integer' || prop.type === 'number') return 'number';
  return 'json';
}

export function DynamicParamForm({
  schema,
  values,
  onChange,
  errors = {},
}: DynamicParamFormProps) {
  const parsed = parseSchema(schema);
  const properties = parsed.properties ?? {};
  const required = new Set(parsed.required ?? []);

  const entries = Object.entries(properties);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        This report requires no additional parameters.
      </p>
    );
  }

  function update(key: string, value: unknown) {
    onChange({ ...values, [key]: value });
  }

  return (
    <div className="flex flex-col gap-4">
      {entries.map(([key, prop]) => {
        const label = prop.title ?? key;
        const isRequired = required.has(key);
        const fieldType = getFieldType(prop);
        const error = errors[key];
        const id = `param-${key}`;

        if (fieldType === 'select') {
          const opts = (prop.enum ?? []).map((v) => ({ value: v, label: v }));
          return (
            <FormSelect
              key={key}
              id={id}
              label={label}
              required={isRequired}
              error={error}
              options={opts}
              value={(values[key] as string) ?? ''}
              onChange={(e) => update(key, e.target.value)}
            />
          );
        }

        if (fieldType === 'date') {
          return (
            <FormField
              key={key}
              label={label}
              required={isRequired}
              error={error}
              htmlFor={id}
            >
              <Input
                id={id}
                type="date"
                error={!!error}
                value={(values[key] as string) ?? ''}
                onChange={(e) => update(key, e.target.value)}
              />
            </FormField>
          );
        }

        if (fieldType === 'number') {
          return (
            <FormField
              key={key}
              label={label}
              required={isRequired}
              error={error}
              htmlFor={id}
            >
              <Input
                id={id}
                type="number"
                error={!!error}
                value={(values[key] as string) ?? ''}
                onChange={(e) =>
                  update(key, e.target.value === '' ? '' : +e.target.value)
                }
              />
            </FormField>
          );
        }

        if (fieldType === 'text') {
          return (
            <FormField
              key={key}
              label={label}
              required={isRequired}
              error={error}
              htmlFor={id}
            >
              <Input
                id={id}
                type="text"
                error={!!error}
                value={(values[key] as string) ?? ''}
                onChange={(e) => update(key, e.target.value)}
              />
            </FormField>
          );
        }

        // JSON textarea fallback for unsupported schema types
        const jsonValue =
          values[key] === undefined
            ? ''
            : typeof values[key] === 'string'
              ? (values[key] as string)
              : JSON.stringify(values[key], null, 2);

        return (
          <FormField
            key={key}
            label={`${label} (JSON)`}
            required={isRequired}
            error={error}
            htmlFor={id}
            hint="Enter a valid JSON value"
          >
            <textarea
              id={id}
              rows={4}
              className={[
                'w-full px-3 py-2 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary',
                'font-mono placeholder:text-text-muted resize-y',
                'focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500',
                error ? 'border-danger-500 focus:border-danger-500' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              value={jsonValue}
              onChange={(e) => {
                try {
                  update(key, JSON.parse(e.target.value));
                }
                // allow-silent-catch: user is actively typing JSON into the textarea; parse failures on incomplete input are expected on nearly every keystroke, so we fall back to storing the raw string rather than logging/surfacing noise
                catch {
                  update(key, e.target.value);
                }
              }}
            />
          </FormField>
        );
      })}
    </div>
  );
}

export const _reportingSkeleton = () => <Skeleton className="h-10 w-full" />;

export const _reportingEmpty = () => <EmptyState title="No data" description="No data found." icon={<span />} />;

export const _reportingError = () => <div className="text-danger-text">Failed to load. Please try again.</div>;
